import { useCallback, useMemo, useState } from "react";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { useSubmitStoplossClaims } from "@/hooks/useSubmitStoplossClaims";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ResourceName } from "@/types";
import { ConfirmLabelType } from "@/types/shared/uiComponents";
import { auraTrackChip, auraTrackState, carrierTrackChip, type TrackChip } from "../../twoTrackState";
import { missingAuraSubmitFields } from "../../auraSubmitValidation";
import { useStoplossClaimContext } from "../../hooks/useStoplossClaimContext";

/**
 * The two submission tracks for the rail and its collapsed bar (#7582). One
 * place derives chip, button label, disabled state and the reason, so the two
 * surfaces never disagree. Vocabulary follows the claimant Claims tab (#5378):
 * "Request funding" / "Mark as submitted"; "Aura funding" / "Carrier".
 */
export interface TrackView {
  /** Chip when the track has a state to show; null while it is only an action. */
  chip: TrackChip | null;
  /** Button label; null when there is no action (done, or not applicable). */
  actionLabel: string | null;
  disabled: boolean;
  /** Why the action is disabled, in plain words; empty when enabled. */
  reason: string;
  onAction: () => void;
  pending: boolean;
}

export const HOLD_REASON = "Waiting on the deductible review";

export const useTrackActions = () => {
  const { claim, policyAccelEnabled, reviewPending, policy } = useStoplossClaimContext();
  const { canEdit } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const { submit } = useSubmitStoplossClaims();
  const [pendingTrack, setPendingTrack] = useState<"aura" | "carrier" | null>(null);
  const [confirmCarrier, setConfirmCarrier] = useState(false);

  const run = useCallback(
    async (track: "aura" | "carrier") => {
      setPendingTrack(track);
      try {
        await submit([claim], track);
      } finally {
        setPendingTrack(null);
      }
    },
    [claim, submit]
  );

  const missing = useMemo(() => missingAuraSubmitFields(claim), [claim]);

  const aura = useMemo<TrackView>(() => {
    const state = auraTrackState(claim);
    const chip = auraTrackChip(claim);
    // "Not applicable" only once a policy is loaded and says so. With no policy
    // linked yet the track stays visible so the readiness list can point at the gap.
    if (policy && !policyAccelEnabled) {
      return {
        chip: { label: "Not applicable to this policy", color: "muted" },
        actionLabel: null,
        disabled: true,
        reason: "This policy is not Accel-enabled.",
        onAction: () => {},
        pending: false,
      };
    }
    const actionLabel = state === "rejected" ? "Retry request" : state === "not_requested" ? "Request funding" : null;
    const reasons: string[] = [];
    if (reviewPending) reasons.push(HOLD_REASON);
    if (missing.length) reasons.push(`Missing: ${missing.join(", ")}`);
    return {
      chip: chip ?? { label: "Not requested", color: "default" },
      actionLabel: canEdit ? actionLabel : null,
      disabled: reasons.length > 0,
      reason: reasons.join(" · "),
      onAction: () => void run("aura"),
      pending: pendingTrack === "aura",
    };
  }, [claim, policyAccelEnabled, policy, reviewPending, missing, canEdit, run, pendingTrack]);

  const carrier = useMemo<TrackView>(() => {
    const chip = carrierTrackChip(claim);
    const done = !!claim.carrier_submitted_at;
    return {
      chip: chip ?? { label: "Not submitted", color: "default" },
      actionLabel: canEdit && !done ? "Mark as submitted" : null,
      disabled: reviewPending,
      reason: reviewPending ? HOLD_REASON : "",
      onAction: () => setConfirmCarrier(true),
      pending: pendingTrack === "carrier",
    };
  }, [claim, canEdit, reviewPending, pendingTrack]);

  const confirmDialog = (
    <ConfirmationDialog
      open={confirmCarrier}
      title="Mark as submitted to carrier"
      message="Record that this claim was sent to the stop-loss carrier? This stamps today's date and moves the claim to Submitted."
      confirmLabel={ConfirmLabelType.Submit}
      isLoading={pendingTrack === "carrier"}
      onCancel={() => setConfirmCarrier(false)}
      onConfirm={() => {
        setConfirmCarrier(false);
        void run("carrier");
      }}
    />
  );

  return { aura, carrier, missingAuraFields: missing, confirmDialog, canEdit };
};
